'use client';

/**
 * Cart Store - Zustand-based cart management with localStorage persistence
 *
 * This module provides cart state management that persists across
 * locale changes by using localStorage per store
 */

import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';
import { trackAddedToCartIfReferred } from '@/lib/linkTracking';

export type CartItem = {
  productId: string;
  variantId?: string; // For products with variants (size, color, etc.)
  name: string;
  price: number;
  quantity: number;
  imageUrl?: string;
  // Optional: Store variant details for display
  variantDetails?: {
    size?: string;
    color?: string;
    dosage?: string;
    form?: string;
    [key: string]: string | undefined;
  };
  // Food product selections
  foodSelections?: {
    optionGroups: Array<{ groupId: string; optionIds: string[] }>;
    additionals: Array<{ additionalId: string; quantity: number }>;
  };
};

export type CartCoupon = {
  id: string;
  code: string;
  type: 'AMOUNT' | 'PERCENTAGE';
  appliesTo: 'ORDER_SUBTOTAL' | 'DELIVERY_FEE' | 'ORDER_TOTAL' | 'PRODUCT_SUBTOTAL';
  amount: number;
  productId?: string | null;
  productName?: string | null;
};

export type CartState = {
  storeId: string;
  items: CartItem[];
  sharedCartId: string | null; /// Set when cart was loaded from a shared cart link (?cart=id)
  checkoutIntentId: string | null; /// Set when a CheckoutIntent was created/resumed for this cart (abandoned-checkout recovery)
  prefillPhone: string | null; /// Set when cart was created for a specific customer (phone locked in checkout)
  coupon: CartCoupon | null;
};

type CartStore = CartState & {
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (productId: string, variantId?: string, foodSelections?: CartItem['foodSelections']) => void;
  updateQuantity: (productId: string, quantity: number, variantId?: string, foodSelections?: CartItem['foodSelections']) => void;
  clearCart: () => void;
  replaceCart: (storeId: string, items: CartItem[], sharedCartId?: string | null, prefillPhone?: string | null) => void;
  getTotal: () => number;
  getItemCount: () => number;
  setStoreId: (storeId: string) => void;
  setCoupon: (coupon: CartCoupon | null) => void;
  clearCoupon: () => void;
  setCheckoutIntentId: (checkoutIntentId: string | null) => void;
};

const CART_STORAGE_KEY = 'vupt-market-cart-v2';
const CART_STORAGE_VERSION = 1;

/** Legacy: array of items only. Current: items + optional shared cart id from ?cart=. */
type StoredCartSnapshot = {
  items: CartItem[];
  sharedCartId?: string | null;
  checkoutIntentId?: string | null;
  prefillPhone?: string | null;
  coupon?: CartCoupon | null;
};

type CartSnapshotResult = {
  items: CartItem[];
  sharedCartId: string | null;
  checkoutIntentId: string | null;
  prefillPhone: string | null;
  coupon: CartCoupon | null;
};

type StoredCartCollection = {
  version: number;
  carts: Record<string, CartItem[] | StoredCartSnapshot>;
};

function normalizeStoredCartEntry(
  entry: CartItem[] | StoredCartSnapshot | undefined
): CartSnapshotResult {
  if (!entry) {
    return { items: [], sharedCartId: null, checkoutIntentId: null, prefillPhone: null, coupon: null };
  }
  if (Array.isArray(entry)) {
    return { items: entry, sharedCartId: null, checkoutIntentId: null, prefillPhone: null, coupon: null };
  }
  return {
    items: Array.isArray(entry.items) ? entry.items : [],
    sharedCartId: entry.sharedCartId ?? null,
    checkoutIntentId: entry.checkoutIntentId ?? null,
    prefillPhone: entry.prefillPhone ?? null,
    coupon: entry.coupon ?? null,
  };
}

type PersistedStorageValue = {
  state: CartState;
  version: number;
};

const isBrowser = typeof window !== 'undefined';

const getStoreIdFromLocation = (): string => {
  if (!isBrowser) {
    return '';
  }

  const pathSegments = window.location.pathname
    .split('/')
    .filter(Boolean);

  // Route format: /{locale}/{storeId}/...
  return pathSegments[1] ?? '';
};

const readStoredCarts = (key: string): StoredCartCollection => {
  if (!isBrowser) {
    return { version: CART_STORAGE_VERSION, carts: {} };
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return { version: CART_STORAGE_VERSION, carts: {} };
    }

    const parsed = JSON.parse(raw) as StoredCartCollection;
    if (parsed && typeof parsed === 'object' && parsed.carts) {
      return {
        version: CART_STORAGE_VERSION,
        carts: parsed.carts,
      };
    }

    return { version: CART_STORAGE_VERSION, carts: {} };
  } catch {
    return { version: CART_STORAGE_VERSION, carts: {} };
  }
};

const writeStoredCarts = (key: string, data: StoredCartCollection) => {
  if (!isBrowser) {
    return;
  }

  window.localStorage.setItem(
    key,
    JSON.stringify({
      version: CART_STORAGE_VERSION,
      carts: data.carts,
    })
  );
};

const getStoredCartSnapshot = (key: string, storeId: string): CartSnapshotResult => {
  if (!storeId) {
    return { items: [], sharedCartId: null, checkoutIntentId: null, prefillPhone: null, coupon: null };
  }

  const stored = readStoredCarts(key);
  const { items, sharedCartId, checkoutIntentId, prefillPhone, coupon } = normalizeStoredCartEntry(stored.carts[storeId]);
  return { items: [...items], sharedCartId, checkoutIntentId, prefillPhone, coupon };
};

const multiStoreStorage: PersistStorage<Partial<CartState>> = {
  getItem: (name) => {
    if (!isBrowser) {
      return null;
    }

    const storeId = getStoreIdFromLocation();
    const { items, sharedCartId, checkoutIntentId, prefillPhone, coupon } = getStoredCartSnapshot(name, storeId);

    return {
      state: {
        storeId,
        items,
        sharedCartId,
        checkoutIntentId,
        prefillPhone,
        coupon,
      },
      version: CART_STORAGE_VERSION,
    };
  },
  setItem: (name, value) => {
    if (!isBrowser) {
      return;
    }

    const targetStoreId = value?.state?.storeId;
    if (!targetStoreId) {
      return;
    }

    const stored = readStoredCarts(name);

    if (Array.isArray(value.state.items) && value.state.items.length > 0) {
      stored.carts[targetStoreId] = {
        items: value.state.items,
        sharedCartId: value.state.sharedCartId ?? null,
        checkoutIntentId: value.state.checkoutIntentId ?? null,
        prefillPhone: value.state.prefillPhone ?? null,
        coupon: value.state.coupon ?? null,
      };
    } else {
      delete stored.carts[targetStoreId];
    }

    writeStoredCarts(name, stored);
  },
  removeItem: (name) => {
    if (!isBrowser) {
      return;
    }

    window.localStorage.removeItem(name);
  },
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      storeId: '',
      items: [],
      sharedCartId: null,
      checkoutIntentId: null,
      prefillPhone: null,
      coupon: null,

      setStoreId: (storeId: string) => {
        const currentStoreId = get().storeId;

        if (!storeId) {
          set({ storeId: '', items: [], sharedCartId: null, checkoutIntentId: null, prefillPhone: null, coupon: null });
          return;
        }

        // If switching stores, load persisted cart + shared cart link + coupon for the new store
        if (currentStoreId !== storeId) {
          const { items, sharedCartId, checkoutIntentId, prefillPhone, coupon } = getStoredCartSnapshot(CART_STORAGE_KEY, storeId);
          set({ storeId, items, sharedCartId, checkoutIntentId, prefillPhone, coupon });
        }
      },

      addItem: (item: Omit<CartItem, 'quantity'>, quantity = 1) => {
        const { items, storeId } = get();
        if (storeId) trackAddedToCartIfReferred(storeId);

        // For Food products, compare by productId + foodSelections
        // For other products, compare by productId + variantId
        const existingItemIndex = items.findIndex((cartItem) => {
          if (cartItem.productId !== item.productId) return false;

          // Food products: compare selections
          if (item.foodSelections || cartItem.foodSelections) {
            const itemSelections = JSON.stringify(item.foodSelections || {});
            const cartSelections = JSON.stringify(cartItem.foodSelections || {});
            return itemSelections === cartSelections;
          }

          // Regular variants: compare variantId
          return cartItem.variantId === item.variantId;
        });

        let newItems: CartItem[];

        if (existingItemIndex >= 0) {
          // Update quantity if item already exists
          newItems = [...items];
          newItems[existingItemIndex] = {
            ...newItems[existingItemIndex],
            quantity: newItems[existingItemIndex].quantity + quantity,
          };
        } else {
          // Add new item
          newItems = [...items, { ...item, quantity }];
        }

        set({ items: newItems });
      },

      removeItem: (productId: string, variantId?: string, foodSelections?: CartItem['foodSelections']) => {
        const { items } = get();
        const newItems = items.filter((item) => {
          if (item.productId !== productId) return true;

          // Food products: compare selections
          if (foodSelections || item.foodSelections) {
            const itemSelections = JSON.stringify(item.foodSelections || {});
            const cartSelections = JSON.stringify(foodSelections || {});
            return itemSelections !== cartSelections;
          }

          // Regular variants: compare variantId
          if (variantId !== undefined) {
            return item.variantId !== variantId;
          }
          return false;
        });
        set({ items: newItems });
      },

      updateQuantity: (productId: string, quantity: number, variantId?: string, foodSelections?: CartItem['foodSelections']) => {
        if (quantity <= 0) {
          get().removeItem(productId, variantId, foodSelections);
          return;
        }

        const { items } = get();
        const newItems = items.map((item) => {
          if (item.productId !== productId) return item;

          // Food products: compare selections
          if (foodSelections || item.foodSelections) {
            const itemSelections = JSON.stringify(item.foodSelections || {});
            const cartSelections = JSON.stringify(foodSelections || {});
            return itemSelections === cartSelections
              ? { ...item, quantity }
              : item;
          }

          // Regular variants: compare variantId
          if (variantId !== undefined) {
            return item.variantId === variantId
              ? { ...item, quantity }
              : item;
          }
          return { ...item, quantity };
        });

        set({ items: newItems });
      },

      clearCart: () => {
        set({ items: [], sharedCartId: null, checkoutIntentId: null, prefillPhone: null, coupon: null });
      },

      setCheckoutIntentId: (checkoutIntentId: string | null) => {
        set({ checkoutIntentId });
      },

      setCoupon: (coupon: CartCoupon | null) => {
        set({ coupon });
      },

      clearCoupon: () => {
        set({ coupon: null });
      },

      replaceCart: (storeId: string, items: CartItem[], sharedCartId?: string | null, prefillPhone?: string | null) => {
        set({ storeId, items, sharedCartId: sharedCartId ?? null, checkoutIntentId: null, prefillPhone: prefillPhone ?? null });
      },

      getTotal: () => {
        const { items } = get();
        return items.reduce(
          (total, item) => total + item.price * item.quantity,
          0
        );
      },

      getItemCount: () => {
        const { items } = get();
        return items.reduce((count, item) => count + item.quantity, 0);
      },
    }),
    {
      name: CART_STORAGE_KEY,
      storage: multiStoreStorage,
      version: CART_STORAGE_VERSION,
      // Partition by store - only restore cart if storeId matches
      partialize: (state): Partial<CartState> => ({
        storeId: state.storeId,
        items: state.items,
        sharedCartId: state.sharedCartId,
        checkoutIntentId: state.checkoutIntentId,
        prefillPhone: state.prefillPhone,
        coupon: state.coupon,
      }),
    }
  )
);

// Helper hook to initialize cart with store
export function useInitializeCart(storeId: string) {
  const setStoreId = useCartStore((state) => state.setStoreId);

  // Initialize store on mount
  React.useEffect(() => {
    setStoreId(storeId);
  }, [storeId, setStoreId]);
}

// For backward compatibility and SSR
import React from 'react';
